import { useEffect, useRef } from "react";
import { useChatStore } from "@/store/chatStore";
import { formatMessageTime } from "@/libs/utils";
import { cn } from "@/libs/utils";
import { useGetUserStatus, useMessagesInfinite, useUserData } from "@/hooks/use-queries";
import { Message } from "@/services/apiService";
import { UserProfileImage } from "@/components/ui/avatar";

export function ChatMessages() {
  const selectedRoomId = useChatStore((state) => state.selectedRoomId);

  const { data: messagesData } = useMessagesInfinite(selectedRoomId!);
  const messages =
    messagesData?.pages.flatMap((page) => page.results).flat() ?? [];

  // Auto scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  if (!selectedRoomId) {
    return (
      <div className="flex-1 flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-sm text-muted-foreground mb-2">
            Select a chat to start messaging
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-background">
      {!!messages && messages?.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              No messages yet. Start the conversation!
            </p>
          </div>
        </div>
      ) : (
        messages.reverse().map((message) => (
          <MessageItem key={message.id} message={message} />
        ))
      )}
      <div ref={messagesEndRef} />
    </div>
  );
}

function MessageItem({ message }: { message: Message }) {
  const {data: currentUser} = useGetUserStatus();
  const {data: senderData} = useUserData({userId:message.sender_id});
  const currentUserId = currentUser?.id;

  const isCurrentUser = message.sender_id === currentUserId;
  // const isOptimistic = message.id.startsWith("temp-");
  const isOptimistic = message.optimistic || false;

  return (
    <div
      className={cn(
        "flex gap-3 items-center",
        (isCurrentUser || isOptimistic) ? "ml-auto flex-row-reverse" : "mr-auto",
        isOptimistic && "opacity-75"
      )}
      style={{
        maxWidth: isCurrentUser ? "min(85%, 600px)" : "min(85%, 600px)",
        width: "fit-content",
      }}
    >
      {isOptimistic?
      <UserProfileImage userId={currentUserId} />
      :
      <UserProfileImage userId={message.sender_id} />
      }

      <div className={cn("flex flex-col", isCurrentUser && "items-end")}>
        {!isCurrentUser && (
          <p className="text-xs text-muted-foreground mb-1">
            {senderData?.username}
          </p>
        )}

        <div
          className={cn(
            "px-4 py-2 rounded-2xl relative break-words",
            isCurrentUser
              ? "bg-primary text-primary-foreground rounded-br-sm"
              : "bg-muted text-foreground rounded-bl-sm",
            isOptimistic && "animate-pulse"
          )}
          style={{
            maxWidth: "100%",
            wordWrap: "break-word",
            overflowWrap: "break-word",
          }}
        >
          <p className="text-sm">{message.text_content}</p>
          {isOptimistic && (
            <div className="absolute -bottom-1 -right-1">
              <div className="w-2 h-2 bg-foreground/50 rounded-full animate-ping" />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 mt-1">
          <p className="text-xs text-muted-foreground">
            {formatMessageTime(message.timestamp)}
          </p>
          {isOptimistic && (
            <span className="text-xs text-muted-foreground italic">
              Sending...
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
